// The slot type viewer — a 3D stage for inspecting a slot type's contract.
// Shows a translucent bounding box representing the slot's spatial envelope,
// with the fitting content models switchable inside it and host models
// (those with sockets of this type) switchable alongside.

import * as THREE from "three";
import {
  instantiateModel,
  ModelInstance,
  bakeSockets,
  buildModel,
  applySlots,
  type Model,
  type SlotType,
  type SocketFill,
} from "../lib";
import { addStudioLights } from "./lights";

export interface SlotTypeViewerOptions {
  renderThumb(canvas: HTMLCanvasElement, model: Model, variantId: string, tight?: boolean): void;
  onClose(): void;
}

const WORLD_COLOR = "#e9edf3";
const PITCH = THREE.MathUtils.degToRad(28);
const AZIM = THREE.MathUtils.degToRad(35);
const MIN_PITCH = 0.08;
const MAX_PITCH = 1.45;
const ROT_SPEED = 0.01;
const ZOOM_MIN = 0.15;
const ZOOM_MAX = 30;
const SPIN_RATE = 0.6;
const GLIDE_DECAY = 0.0023;
const GLIDE_STOP = 0.02;
const PED_H = 0.1;

// The envelope dimensions are parsed from the carry slot type's instructions,
// but since slot types don't carry structured dimension fields yet, we use a
// sensible default that matches the carry contract (0.2w x 0.15h x 0.15d).
// When the slot type system gains structured dimension fields, read them here.
const DEFAULT_ENVELOPE = { w: 0.2, h: 0.15, d: 0.15 };

const el = (id: string): HTMLElement => document.getElementById(id)!;

export class SlotTypeViewer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 200);
  private readonly pivot = new THREE.Object3D();
  private readonly modelMaterial = new THREE.MeshBasicMaterial({ vertexColors: true });
  private readonly canvas: HTMLCanvasElement;
  private readonly stage: HTMLElement;
  private pedestal?: THREE.Mesh;
  private envelope?: THREE.LineSegments;

  private instance?: ModelInstance;
  private socketMesh?: THREE.Mesh;
  private socketGeo?: THREE.BufferGeometry;

  private slotType!: SlotType;
  private contentModels: Model[] = [];
  private hostModels: Model[] = [];
  private contentIndex = 0;
  private hostIndex = -1; // -1 = content-only mode (no host)

  private stageW = 1;
  private stageH = 1;
  private running = false;
  private spin = true;
  private spinAngle = 0;
  private userYaw = 0;
  private userPitch = 0;
  private zoom = 1.4;
  private readonly target = new THREE.Vector3(0, 0.5, 0);
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private dragging = false;
  private pinchStartDist = 0;
  private pinchStartZoom = 1;
  private yawVel = 0;
  private pitchVel = 0;
  private lastMoveTime = 0;
  private prev = 0;

  constructor(private readonly opts: SlotTypeViewerOptions) {
    this.canvas = el("stv-canvas") as HTMLCanvasElement;
    this.stage = el("stv-stage");
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene.background = new THREE.Color(WORLD_COLOR);
    this.scene.fog = new THREE.Fog(WORLD_COLOR, 44, 70);
    addStudioLights(this.scene, true);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(120, 120),
      new THREE.ShadowMaterial({ opacity: 0.18 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    this.pivot.position.y = PED_H;
    this.scene.add(this.pivot);

    this.wireControls();
  }

  open(
    slotType: SlotType,
    contentModels: Model[],
    hostModels: Model[],
  ): void {
    this.slotType = slotType;
    this.contentModels = contentModels;
    this.hostModels = hostModels;
    this.contentIndex = 0;
    this.hostIndex = -1;
    this.userYaw = 0;
    this.userPitch = 0;
    this.spinAngle = 0;
    this.yawVel = 0;
    this.pitchVel = 0;
    this.pointers.clear();
    this.dragging = false;
    this.spin = true;

    this.resize();
    this.updateInfo();
    this.buildContentPicker();
    this.buildHostPicker();
    this.rebuild(true);

    if (!this.running) {
      this.running = true;
      this.prev = performance.now();
      requestAnimationFrame(this.frame);
    }
  }

  close(): void {
    this.running = false;
  }

  // ── Scene build ─────────────────────────────────────────────────────────────
  private rebuild(reframe: boolean): void {
    this.instance?.dispose();
    this.clearSocketMesh();
    for (const child of [...this.pivot.children]) this.pivot.remove(child);

    // Build the bounding envelope
    if (this.envelope) {
      this.scene.remove(this.envelope);
      this.envelope.geometry.dispose();
      (this.envelope.material as THREE.Material).dispose();
    }
    const env = DEFAULT_ENVELOPE;
    this.envelope = this.buildEnvelope(env.w, env.h, env.d);
    this.envelope.position.y = -PED_H; // sit the envelope on the pedestal
    this.pivot.add(this.envelope);

    const content = this.contentModels[this.contentIndex];
    if (!content) {
      // No content — just show the envelope
      if (reframe) this.reframe(env.w, env.h);
      return;
    }

    const host = this.hostIndex >= 0 ? this.hostModels[this.hostIndex] : undefined;
    if (host) {
      // Mounted mode: show host with content in its socket
      const socket = host.sockets.find((s) => s.slotType === this.slotType.id);
      if (socket) {
        const fills: SocketFill[] = [{ socket: socket.id, model: content }];
        this.socketGeo = buildModel(applySlots(bakeSockets(host, fills), undefined));
        this.socketMesh = new THREE.Mesh(this.socketGeo, this.modelMaterial);
        this.socketMesh.castShadow = this.socketMesh.receiveShadow = true;
        this.pivot.add(this.socketMesh);

        const bb = (this.socketGeo.boundingBox ?? new THREE.Box3().setFromObject(this.socketMesh)).clone();
        const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
        this.socketMesh.position.set(-cx, -bb.min.y, -cz);
        // Position envelope at the socket position, offset by the model centering
        this.envelope.position.set(
          socket.position[0] - cx,
          socket.position[1] - bb.min.y,
          socket.position[2] - cz,
        );

        const radius = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) / 2 || 0.2;
        const height = Math.max(bb.max.y - bb.min.y, 0.1);
        if (reframe) this.reframe(radius, height);
      }
    } else {
      // Content-only mode: show content inside the envelope
      this.instance = instantiateModel(content, { material: this.modelMaterial });
      this.instance.setVariant(content.defaultVariantId);
      this.pivot.add(this.instance.object);

      const bb = this.instance.bounds;
      const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
      this.instance.object.position.set(-cx, -bb.min.y, -cz);
      this.envelope.position.set(-cx, -bb.min.y, -cz);

      const radius = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z, env.w) / 2 || 0.2;
      const height = Math.max(bb.max.y - bb.min.y, env.h, 0.1);
      if (reframe) this.reframe(radius, height);
    }
  }

  private buildEnvelope(w: number, h: number, d: number): THREE.LineSegments {
    const hw = w / 2, hd = d / 2;
    // 12 edges of a box
    const verts = [
      // bottom face
      -hw, 0, -hd,  hw, 0, -hd,
       hw, 0, -hd,  hw, 0,  hd,
       hw, 0,  hd, -hw, 0,  hd,
      -hw, 0,  hd, -hw, 0, -hd,
      // top face
      -hw, h, -hd,  hw, h, -hd,
       hw, h, -hd,  hw, h,  hd,
       hw, h,  hd, -hw, h,  hd,
      -hw, h,  hd, -hw, h, -hd,
      // verticals
      -hw, 0, -hd, -hw, h, -hd,
       hw, 0, -hd,  hw, h, -hd,
       hw, 0,  hd,  hw, h,  hd,
      -hw, 0,  hd, -hw, h,  hd,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    const mat = new THREE.LineBasicMaterial({
      color: "#5b8def",
      transparent: true,
      opacity: 0.7,
      depthTest: true,
    });
    return new THREE.LineSegments(geo, mat);
  }

  private reframe(radius: number, height: number): void {
    if (this.pedestal) {
      this.scene.remove(this.pedestal);
      (this.pedestal.geometry as THREE.BufferGeometry).dispose();
    }
    const pr = Math.max(radius * 1.3, 0.18);
    this.pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(pr, pr * 1.08, PED_H, 32),
      new THREE.MeshLambertMaterial({ color: "#cdd5e1" }),
    );
    this.pedestal.position.y = PED_H / 2;
    this.pedestal.receiveShadow = true;
    this.scene.add(this.pedestal);
    this.zoom = Math.max(radius, height) * 1.5 + 0.25;
    this.target.set(0, PED_H + height * 0.45, 0);
    this.applyCamera();
  }

  private clearSocketMesh(): void {
    if (this.socketMesh) { this.pivot.remove(this.socketMesh); this.socketMesh = undefined; }
    if (this.socketGeo) { this.socketGeo.dispose(); this.socketGeo = undefined; }
  }

  // ── Info panel ──────────────────────────────────────────────────────────────
  private updateInfo(): void {
    el("stv-name").textContent = this.slotType.label;
    el("stv-summary").textContent = this.slotType.summary;
    const instr = this.slotType.instructions + (this.slotType.description ? "\n\n" + this.slotType.description : "");
    el("stv-instr").textContent = instr;
    (el("stv-instr-wrap") as HTMLDetailsElement).open = false;
    el("stv-instr-wrap").hidden = !instr;
  }

  // ── Content picker ──────────────────────────────────────────────────────────
  private buildContentPicker(): void {
    const wrap = el("stv-content-wrap");
    const grid = el("stv-content");
    grid.innerHTML = "";
    if (this.contentModels.length === 0) { wrap.hidden = true; return; }
    wrap.hidden = false;
    el("stv-content-count").textContent = String(this.contentModels.length);
    this.contentModels.forEach((m, i) => {
      const chip = document.createElement("button");
      chip.className = "variant-chip";
      chip.title = m.name;
      chip.innerHTML = `<span class="variant-thumb"><canvas width="240" height="240"></canvas></span><span class="stv-chip-name">${m.name}</span>`;
      this.opts.renderThumb(chip.querySelector("canvas") as HTMLCanvasElement, m, m.defaultVariantId, true);
      chip.addEventListener("click", () => this.selectContent(i));
      grid.appendChild(chip);
    });
    this.markActiveContent();
  }

  private selectContent(i: number): void {
    if (i === this.contentIndex) return;
    this.contentIndex = i;
    this.rebuild(true);
    this.markActiveContent();
  }

  private markActiveContent(): void {
    const chips = el("stv-content").children;
    for (let i = 0; i < chips.length; i++) chips[i].classList.toggle("active", i === this.contentIndex);
  }

  // ── Host picker ─────────────────────────────────────────────────────────────
  private buildHostPicker(): void {
    const wrap = el("stv-host-wrap");
    const grid = el("stv-host");
    grid.innerHTML = "";
    if (this.hostModels.length === 0) { wrap.hidden = true; return; }
    wrap.hidden = false;
    el("stv-host-count").textContent = String(this.hostModels.length);

    // "None" option — content-only
    const none = document.createElement("button");
    none.className = "variant-chip stv-none-chip";
    none.title = "Content only";
    none.innerHTML = `<span class="variant-thumb stv-thumb-empty">--</span><span class="stv-chip-name">Content only</span>`;
    none.addEventListener("click", () => this.selectHost(-1));
    grid.appendChild(none);

    this.hostModels.forEach((m, i) => {
      const chip = document.createElement("button");
      chip.className = "variant-chip";
      chip.title = m.name;
      chip.innerHTML = `<span class="variant-thumb"><canvas width="240" height="240"></canvas></span><span class="stv-chip-name">${m.name}</span>`;
      this.opts.renderThumb(chip.querySelector("canvas") as HTMLCanvasElement, m, m.defaultVariantId, true);
      chip.addEventListener("click", () => this.selectHost(i));
      grid.appendChild(chip);
    });
    this.markActiveHost();
  }

  private selectHost(i: number): void {
    if (i === this.hostIndex) return;
    this.hostIndex = i;
    this.rebuild(true);
    this.markActiveHost();
  }

  private markActiveHost(): void {
    const chips = el("stv-host").children;
    // index 0 is the "None" chip, then hosts start at 1
    for (let i = 0; i < chips.length; i++) {
      chips[i].classList.toggle("active", i === this.hostIndex + 1);
    }
  }

  // ── Camera ──────────────────────────────────────────────────────────────────
  private applyCamera(): void {
    const aspect = this.stageW / this.stageH;
    const c = this.camera;
    c.top = this.zoom; c.bottom = -this.zoom;
    c.left = -this.zoom * aspect; c.right = this.zoom * aspect;
    const azim = AZIM + this.spinAngle + this.userYaw;
    const pitch = THREE.MathUtils.clamp(PITCH + this.userPitch, MIN_PITCH, MAX_PITCH);
    const cp = Math.cos(pitch), sp = Math.sin(pitch);
    c.position.set(
      this.target.x + 40 * cp * Math.sin(azim),
      this.target.y + 40 * sp,
      this.target.z + 40 * cp * Math.cos(azim),
    );
    c.up.set(0, 1, 0);
    c.lookAt(this.target);
    c.updateProjectionMatrix();
  }

  private resize(): void {
    this.stageW = this.stage.clientWidth || 1;
    this.stageH = this.stage.clientHeight || 1;
    this.renderer.setSize(this.stageW, this.stageH, false);
    this.applyCamera();
  }

  // ── Controls ────────────────────────────────────────────────────────────────
  private wireControls(): void {
    el("stv-back").addEventListener("click", () => this.opts.onClose());

    this.canvas.addEventListener("pointerdown", (e) => {
      this.canvas.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.dragging = true;
      this.yawVel = 0; this.pitchVel = 0;
      this.lastMoveTime = e.timeStamp;
      if (this.pointers.size === 2) this.beginPinch();
    });
    this.canvas.addEventListener("pointermove", (e) => {
      const p = this.pointers.get(e.pointerId);
      if (!p) return;
      const dx = e.clientX - p.x, dy = e.clientY - p.y;
      p.x = e.clientX; p.y = e.clientY;
      this.spin = false;
      if (this.pointers.size >= 2) { this.updatePinch(); return; }
      this.orbit(dx, dy, e.timeStamp);
    });
    const endPointer = (e: PointerEvent): void => {
      if (!this.pointers.delete(e.pointerId)) return;
      this.dragging = this.pointers.size > 0;
      if (this.pointers.size === 2) this.beginPinch();
    };
    this.canvas.addEventListener("pointerup", endPointer);
    this.canvas.addEventListener("pointercancel", endPointer);
    this.canvas.addEventListener("wheel", (e) => {
      e.preventDefault();
      this.zoom = THREE.MathUtils.clamp(this.zoom * (e.deltaY > 0 ? 1.1 : 0.9), ZOOM_MIN, ZOOM_MAX);
      this.applyCamera();
    }, { passive: false });

    window.addEventListener("resize", () => { if (this.running) this.resize(); });
  }

  private orbit(dx: number, dy: number, time: number): void {
    const dYaw = -dx * ROT_SPEED;
    const dPitch = dy * ROT_SPEED;
    this.userYaw += dYaw;
    this.userPitch = THREE.MathUtils.clamp(this.userPitch + dPitch, MIN_PITCH - PITCH, MAX_PITCH - PITCH);
    const dt = (time - this.lastMoveTime) / 1000;
    if (dt > 0 && dt < 0.1) { this.yawVel = dYaw / dt; this.pitchVel = dPitch / dt; }
    this.lastMoveTime = time;
  }

  private pinchDistance(): number {
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
  }

  private beginPinch(): void {
    this.pinchStartDist = this.pinchDistance();
    this.pinchStartZoom = this.zoom;
    this.yawVel = 0; this.pitchVel = 0;
  }

  private updatePinch(): void {
    const d = this.pinchDistance();
    if (d <= 0 || this.pinchStartDist <= 0) return;
    this.zoom = THREE.MathUtils.clamp(this.pinchStartZoom * this.pinchStartDist / d, ZOOM_MIN, ZOOM_MAX);
    this.applyCamera();
  }

  // ── Frame loop ──────────────────────────────────────────────────────────────
  private frame = (now: number): void => {
    if (!this.running) return;
    requestAnimationFrame(this.frame);
    const dt = Math.min((now - this.prev) / 1000, 0.05); this.prev = now;
    if (this.spin && !this.dragging) this.spinAngle -= dt * SPIN_RATE;
    if (!this.dragging && (this.yawVel !== 0 || this.pitchVel !== 0)) {
      this.userYaw += this.yawVel * dt;
      const pitch = THREE.MathUtils.clamp(this.userPitch + this.pitchVel * dt, MIN_PITCH - PITCH, MAX_PITCH - PITCH);
      if (pitch === this.userPitch) this.pitchVel = 0;
      this.userPitch = pitch;
      const decay = Math.pow(GLIDE_DECAY, dt);
      this.yawVel *= decay; this.pitchVel *= decay;
      if (Math.abs(this.yawVel) < GLIDE_STOP) this.yawVel = 0;
      if (Math.abs(this.pitchVel) < GLIDE_STOP) this.pitchVel = 0;
    }
    this.applyCamera();
    this.renderer.render(this.scene, this.camera);
  };
}
